import { useLazyQuery, useMutation } from '@apollo/client';
import { Divider } from '@material-ui/core';
import { userSelector } from 'common/globalComponents/selectors';
import TableError from 'components/TableError';
import useRequest from 'hooks/useRequest';
import { stagingClient } from 'lib/apollo-server-staging';
import { isEmpty } from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  Badge,
  Card,
  CardBody,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  Table,
  TabPane,
  UncontrolledDropdown,
} from 'reactstrap';
import confirm from 'reactstrap-confirm';
import { createStructuredSelector } from 'reselect';
import {
  ACTIVE_AND_DELETED_ARRAY,
  LANGUAGE_ARRAY_WITH_STRING,
  PRINT_LANGUAGE,
  PRINT_LANGUAGE_COLOR,
  SORT_VALUES,
  SORT_VALUES_DATE,
  taxonomyType,
  USER_ROLE,
} from 'utils/constants';
import { SMKLinks } from 'utils/links';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import Loader from '../../../components/Loader';
import Paginate from '../../../components/Paginate';
import { useRouter } from '../../../hooks/useRouter';
import {
  buildURL,
  checkIfValueExist,
  formatInputDate,
  getImageUrl,
  getTranslationLabel,
  IMAGE_SIZES,
  printDateTimeTwoLines,
  printSerialNumbers,
  urlSlugify,
} from '../../../utils/helper';
import { ADD_RECIPE, DELETE_RECIPE, UPDATE_RECIPE } from '../graphql/recipe.mutation';
import { RECIPE_SEARCH } from '../graphql/recipe.query';

const stateSelector = createStructuredSelector({
  user: userSelector,
});

const RecipeList = () => {
  const { user } = useSelector(stateSelector);

  const router = useRouter();
  const history = useHistory();
  toast.configure();

  const [recipeList, setRecipeList] = useState([]);
  const [recipeListPagination, setRecipeListPagination] = useState({});

  const [activeTab, setActiveTab] = useState('all');

  const TAB_FILTERS = [
    {
      name: 'All',
      filters: {
        active: null,
        deleted: null,
      },
    },
    {
      name: 'Published',
      filters: {
        active: true,
        deleted: false,
      },
    },
    {
      name: 'Draft',
      filters: {
        active: false,
        deleted: false,
      },
    },
  ];

  const [createdByList, setCreatedByList] = useState([]);

  const defaultFilters = {
    active: { value: null, label: 'All' },
    deleted: { value: null, label: 'All' },
    isFeatured: { value: null, label: 'All' },
    language: LANGUAGE_ARRAY_WITH_STRING[0],
    name: '',
    createdBy: null,
    startDate: formatInputDate(moment().subtract(1, 'year')),
    endDate: formatInputDate(moment()),
    categoryIds: null,
  };
  const [filters, setFilters] = useState(defaultFilters);

  const [getStaffUsersRequest, { loading: getStaffUsersLoading }] = useRequest({
    url: `/user/admin/search`,
    method: 'POST',
    onSuccess: (data) => {
      setCreatedByList(
        data?.results?.map((d) => {
          return {
            ...d,
            label: `${d.firstName} ${d.lastName}\n${d.email}`,
            value: d.id,
          };
        })
      );
    },
  });

  const [getRecipeListRequest, { loading: getRecipeListLoading }] = useLazyQuery(RECIPE_SEARCH, {
    onCompleted: (data) => {
      if (!isEmpty(data?.admin_nutrition_recipe_search)) {
        setRecipeList(data?.admin_nutrition_recipe_search?.results);
        setRecipeListPagination(data?.admin_nutrition_recipe_search?.pagination);
      }
    },
    onError: () => {
      toast.error('Something went wrong!');
    },
  });

  const [markActiveInActiveRequest, { loading: markActiveInActiveLoading }] = useMutation(UPDATE_RECIPE, {
    onCompleted: (data) => {
      if (!isEmpty(data?.admin_nutrition_recipe_update)) {
        handleChangePage(parseInt(router?.query?.page, 10) || 1);
        toast.success(`Success! Recipe has been updated.`);
      }
    },
    onError: (error) => {
      toast.error('Something went wrong!');
    },
  });

  const [deleteRecipeRequest, { loading: deleteRecipeLoading }] = useMutation(DELETE_RECIPE, {
    onCompleted: (data) => {
      if (!isEmpty(data?.admin_nutrition_recipe_delete)) {
        handleChangePage(parseInt(router?.query?.page, 10) || 1);
        toast.success(`Success! Recipe has been deleted.`);
      }
    },
    onError: () => {
      toast.error('Something went wrong!');
    },
  });

  const setPageInUrl = (page) => {
    history.push({
      pathname: router.pathname,
      search: `?${new URLSearchParams({ page }).toString()}`,
    });
  };

  const handleChangePage = async (page, data) => {
    setPageInUrl(page);

    await getRecipeListRequest({
      variables: {
        searchDTO: {
          page,
          size: 20,
          ...filters,
          active: checkIfValueExist(filters?.active?.value),
          deleted: checkIfValueExist(filters?.deleted?.value),
          isFeatured: checkIfValueExist(filters?.isFeatured?.value),
          language: checkIfValueExist(filters?.language?.value),
          createdBy: filters?.createdBy?.value ? filters?.createdBy?.value : undefined,
          name: filters?.name ? filters?.name : undefined,
          ...SORT_VALUES_DATE,
          ...(!isEmpty(data) ? data : {}),
          createdBy: undefined,
        },
      },
    });
  };

  const applyFilters = () => {
    if (activeTab !== 'all') {
      setPageInUrl(1);
      const currentFilters = TAB_FILTERS?.find((e) => e?.name?.toLowerCase() === activeTab);
      const updatedFilters = {
        ...filters,
        ...currentFilters?.filters,
        isFeatured: undefined,
        language: checkIfValueExist(filters?.language?.value),
        createdById: filters?.createdBy?.value,
      };
      handleChangePage(1, updatedFilters);
    } else {
      handleChangePage(1);
    }
  };

  const clearFilters = async () => {
    setPageInUrl(1);
    let currentTabFilters = {};
    if (activeTab !== 'all') {
      const currentFilters = TAB_FILTERS?.find((e) => e?.name?.toLowerCase() === activeTab);
      currentTabFilters = {
        ...defaultFilters,
        ...currentFilters?.filters,
        isFeatured: undefined,
        language: checkIfValueExist(defaultFilters?.language?.value),
      };
    }
    setFilters({ ...defaultFilters, name: '' });
    await getRecipeListRequest({
      variables: {
        searchDTO: {
          page: 1,
          size: 20,
          ...defaultFilters,
          active: checkIfValueExist(defaultFilters?.active?.value),
          deleted: checkIfValueExist(defaultFilters?.deleted?.value),
          isFeatured: checkIfValueExist(defaultFilters?.isFeatured?.value),
          language: checkIfValueExist(defaultFilters?.language?.value),
          name: '',
          ...SORT_VALUES_DATE,
          ...(!isEmpty(currentTabFilters) ? currentTabFilters : {}),
          createdBy: undefined,
        },
      },
    });
  };

  const handleActive = async (data) => {
    await markActiveInActiveRequest({
      variables: {
        id: data?.id,
        recipeDTO: {
          name: urlSlugify(data?.name),
          active: !data?.active,
        },
      },
    });
  };

  const handleDelete = async (data) => {
    const result = await confirm({
      title: `${data?.deleted ? 'Undelete' : 'Delete'} Recipe`,
      message: `Are you sure, you want to ${data?.deleted ? 'Undelete' : 'Delete'} this recipe?`,
      confirmText: 'Confirm',
      confirmColor: 'primary',
      cancelColor: 'link text-danger',
    });

    if (result) {
      if (!data?.deleted) {
        await deleteRecipeRequest({
          variables: {
            id: data?.id,
          },
        });
      } else {
        await markActiveInActiveRequest({
          variables: {
            id: data?.id,
            recipeDTO: {
              name: data?.name,
              active: false,
              deleted: !data.deleted,
            },
          },
        });
      }
    }
  };

  useEffect(() => {
    handleChangePage(parseInt(router?.query?.page, 10) || 1);
    getStaffUsersRequest({
      body: {
        active: true,
        deleted: false,
        page: 1,
        size: 500,
        roleIds: [USER_ROLE.STAFF],
        ...SORT_VALUES,
      },
    });
  }, []);

  const [cuisinesList, setCuisinesList] = useState([]);

  const [getCuisinesListRequest, { loading: getCuisinesListLoading }] = useRequest({
    url: '/common/admin/taxonomy/search',
    method: 'POST',
    onSuccess: (data) => {
      setCuisinesList(
        data?.results?.map((e) => ({
          label: e?.name,
          value: e?.id,
        }))
      );
    },
    onError: (err) => {
      toast.error(err?.message?.message || err?.message);
    },
  });

  useEffect(() => {
    getCuisinesListRequest({
      body: {
        page: 1,
        size: 500,
        sortBy: 'id',
        sortOrder: 0,
        type: taxonomyType.NUTRITION_CUISINE,
      },
    });
  }, []);

  const [addRecipeRequest, { loading: addRecipeLoading }] = useMutation(ADD_RECIPE, {
    client: stagingClient,
    onCompleted: (data) => {
      if (!isEmpty(data?.admin_nutrition_recipe_add)) {
        applyFilters();
        toast.success('Success! Recipe has been copied to staging.');
      }
    },
    onError: () => {
      toast.error('Recipe already exists!');
    },
  });

  const handleCopyStaging = async (values) => {
    const payload = {
      active: values?.active,
      deleted: values?.deleted,
      isFeatured: values?.isFeatured,
      name: values?.name,
      slug: values?.slug,
      description: values?.description,

      categories: !isEmpty(values?.categories)
        ? values?.categories?.map((e) => ({
            id: e?.id,
          }))
        : [],
      servesCount: values?.servesCount,
      prepTime: +values?.prepTime,
      cookTime: +values?.cookTime,
      difficulty: values?.difficulty,

      instructions: {
        ...values?.instructions,
        directions: !isEmpty(values?.instructions?.directions)
          ? values?.instructions?.directions?.map((elem) => ({
              ...elem,
              ingredientIds: !isEmpty(elem?.ingredientIds)
                ? elem?.ingredientIds?.filter((e) => !isEmpty(e?.value))?.map((e) => e?.value)
                : [],
              utensils: !isEmpty(elem?.utensils) ? elem?.utensils?.map((e) => e?.label) : [],
            }))
          : [],
      },
      nutrition: {
        ...values?.nutrition,
        calories: !isEmpty(String(values?.nutrition?.calories)) ? values?.nutrition?.calories : undefined,
        glycemicIndex: !isEmpty(String(values?.nutrition?.glycemicIndex))
          ? values?.nutrition?.glycemicIndex
          : undefined,
      },

      language: values?.language,
      translations: !isEmpty(values?.translations) ? [values?.translations?.id] : [],
      internalComments: values?.internalComments || [],

      // for SEO Object
      seo: {
        title: !isEmpty(values?.seoTitle) ? values?.seoTitle : '',
        description: !isEmpty(values?.seoDescription) ? values?.seoDescription : '',
        keywords: !isEmpty(values?.seoKeywords) ? values?.seoKeywords?.map((e) => e?.value).join(', ') : '',
        ...(!isEmpty(values?.ogImage)
          ? {
              ogImageId: values?.ogImage?.id,
              ogImage: undefined,
            }
          : { ogImageId: null }),
      },
      ...(!isEmpty(values?.image)
        ? {
            imageId: values?.image?.id,
          }
        : { imageId: null }),

      image: undefined,
      seoTitle: undefined,
      seoDescription: undefined,
      seoKeywords: undefined,
      ogImage: undefined,
    };

    await addRecipeRequest({
      variables: {
        recipeDTO: {
          ...payload,
        },
      },
    });
  };

  return (
    <>
      <div className="page-content">
        <Loader
          isActive={
            getCuisinesListLoading ||
            getRecipeListLoading ||
            markActiveInActiveLoading ||
            deleteRecipeLoading ||
            getStaffUsersLoading ||
            addRecipeLoading
          }
        />

        <Container fluid>
          <Breadcrumbs title="Recipe" breadcrumbItem="Recipe list" />

          <div>
            <Nav tabs>
              {TAB_FILTERS?.map((elem, index) => {
                return (
                  <NavItem key={index}>
                    <NavLink
                      className={activeTab === elem?.name?.toLowerCase() ? 'active' : ''}
                      onClick={() => {
                        setActiveTab(elem?.name?.toLowerCase());
                        setPageInUrl(1);

                        const updatedFilters = {
                          ...filters,
                          ...elem?.filters,
                          isFeatured: undefined,
                          language: filters?.language?.value,
                        };
                        setFilters(updatedFilters);
                        handleChangePage(1, updatedFilters);
                      }}>
                      {elem?.name}
                    </NavLink>
                  </NavItem>
                );
              })}
            </Nav>
            <TabContent activeTab={activeTab}>
              <TabPane tabId={activeTab}>
                <Card>
                  <CardBody>
                    <div className="mt-4">
                      <div className="row">
                        {activeTab !== 'draft' && (
                          <>
                            <div className="col-lg-2 mb-4">
                              <label className="form-label">Active</label>
                              <Select
                                options={ACTIVE_AND_DELETED_ARRAY}
                                value={filters.active}
                                onChange={(value) => setFilters({ ...filters, active: value })}
                              />
                            </div>
                            <div className="col-lg-2 mb-4">
                              <label className="form-label">Deleted</label>
                              <Select
                                options={ACTIVE_AND_DELETED_ARRAY}
                                value={filters.deleted}
                                onChange={(value) => setFilters({ ...filters, deleted: value })}
                              />
                            </div>
                            <div className="col-lg-2 mb-4">
                              <label className="form-label">is Featured</label>
                              <Select
                                options={ACTIVE_AND_DELETED_ARRAY}
                                value={filters.isFeatured}
                                onChange={(value) => setFilters({ ...filters, isFeatured: value })}
                              />
                            </div>
                          </>
                        )}

                        <div className="col-lg-2 mb-4">
                          <label className="form-label">Language</label>
                          <Select
                            options={LANGUAGE_ARRAY_WITH_STRING}
                            value={filters.language}
                            onChange={(value) => setFilters({ ...filters, language: value })}
                          />
                        </div>

                        {activeTab === 'draft' && (
                          <div className="col-lg-4 mb-4">
                            <label className="form-label">CreatedBy</label>
                            <Select
                              options={createdByList}
                              value={filters.createdBy}
                              onChange={(e) => setFilters({ ...filters, createdBy: e })}
                            />
                          </div>
                        )}

                        <div className="col-lg-4 mb-4">
                          <label htmlFor="name" className="form-label">
                            Name
                          </label>
                          <Input
                            type="text"
                            name="name"
                            placeholder="Name..."
                            value={filters.name}
                            onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                          />
                        </div>

                        <div className="col-lg-3 mb-4">
                          <label className="form-label">Created Date - From</label>
                          <Input
                            type="date"
                            name="startDate"
                            placeholder="From Date"
                            value={filters.startDate}
                            max={moment().format('YYYY-MM-DD')}
                            onChange={(e) => setFilters({ ...filters, startDate: formatInputDate(e.target.value) })}
                          />
                        </div>
                        <div className="col-lg-3 mb-4">
                          <label className="form-label">Created Date - To</label>
                          <Input
                            type="date"
                            name="endDate"
                            placeholder="To Date"
                            value={filters.endDate}
                            max={moment().format('YYYY-MM-DD')}
                            onChange={(e) => setFilters({ ...filters, endDate: formatInputDate(e.target.value) })}
                          />
                        </div>
                        <div className="col-lg-3 mb-4">
                          <label className="form-label">Cusines</label>
                          <Select
                            options={cuisinesList}
                            placeholder="Select Cuisines..."
                            value={
                              !isEmpty(filters?.categoryIds)
                                ? cuisinesList?.find((e) => e?.value === filters.categoryIds?.[0])
                                : null
                            }
                            onChange={(e) => setFilters({ ...filters, categoryIds: [e?.value] })}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 pt-2">
                      <a className="btn btn-primary me-4" onClick={applyFilters}>
                        Apply Filters
                      </a>
                      <a className="" onClick={clearFilters}>
                        Clear
                      </a>
                    </div>
                  </CardBody>
                </Card>
              </TabPane>
            </TabContent>
          </div>

          <Row className="mb-2 align-items-center">
            <Col sm="4">
              <div className="search-box me-2 mb-2 d-inline-block">
                <div className="position-relative font-bold">{recipeListPagination?.totalCount} Recipe found</div>
              </div>
            </Col>

            {user.entityPermissions?.nutrition_recipe?.create && (
              <Col sm="8">
                <div className="text-sm-end">
                  <Link
                    replace
                    className="btn btn-success btn-rounded waves-effect waves-light mb-2 me-2"
                    to={SMKLinks.RECIPE_ADD}>
                    <i className="mdi mdi-plus me-1" />
                    Add Recipe
                  </Link>
                </div>
              </Col>
            )}
          </Row>

          <Row>
            <Col lg="12">
              <div className="table-responsive" style={{ minHeight: 300 }}>
                <Table className="project-list-table   table-centered table-borderless">
                  <thead>
                    <tr className="table-warning">
                      <th scope="col">S.No.</th>
                      <th scope="col" style={{ maxWidth: '40px' }}>
                        Image
                      </th>
                      <th scope="col">Name</th>
                      <th scope="col">Translation</th>
                      {activeTab === 'draft' && (
                        <>
                          <th scope="col">Comments</th>
                          <th scope="col">CreatedBy</th>
                        </>
                      )}
                      <th scope="col">Created By</th>
                      <th scope="col">Created Date</th>
                      <th scope="col">Updated Date</th>
                      <th scope="col">Language</th>
                      <th scope="col">is Featured</th>
                      <th scope="col">Active</th>
                      <th scope="col">Deleted</th>
                      <th scope="col"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {!isEmpty(recipeList) ? (
                      recipeList?.map((recipe, index) => {
                        return (
                          <tr key={`_test_${recipe.name}_`} className={recipe?.deleted ? 'bg-danger' : ''}>
                            <td>{printSerialNumbers(recipeListPagination)[index]}</td>
                            <td>
                              {!isEmpty(recipe?.image) && (
                                <img
                                  src={getImageUrl(recipe?.image, IMAGE_SIZES.ORIGINAL)}
                                  className="img-fluid rounded-circle"
                                  style={{ minWidth: '40px', width: '40px', height: '40px' }}
                                />
                              )}
                            </td>
                            <td>
                              <div className="d-flex">
                                <div>
                                  {user.entityPermissions?.nutrition_recipe?.update ? (
                                    <Link
                                      to={buildURL(SMKLinks.RECIPE_EDIT, {
                                        id: recipe?.id,
                                      })}>
                                      {recipe?.name}
                                    </Link>
                                  ) : (
                                    recipe?.name
                                  )}

                                  <br />
                                  {recipe?.slug}
                                </div>
                                {!isEmpty(recipe.slug) && recipe?.active && (
                                  <div className="ms-2">
                                    <a
                                      target="_blank"
                                      rel="noreferrer"
                                      href={`${process.env.REACT_APP_THEPARENTZ_WEB_URL}/recipes/${recipe?.slug}-n600r${recipe?.uid}`}>
                                      <i className="bx bx-link-external fs-4" />
                                    </a>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              {getTranslationLabel(recipe?.translations[0])} <br />
                              {recipe?.translations[0]?.slug}
                            </td>

                            {activeTab === 'draft' && (
                              <>
                                <td>
                                  {recipe?.internalComments?.map((c) => {
                                    return (
                                      <div className="mb-2">
                                        <strong>{c.user_name}</strong> <small>({c.date})</small>
                                        <p className="mb-0 mt-1">{c.comment}</p>
                                      </div>
                                    );
                                  })}
                                </td>
                                <td>{recipe?.createdBy?.displayName}</td>
                              </>
                            )}
                            <td>{recipe?.createdBy?.displayName}</td>
                            <td>{printDateTimeTwoLines(recipe?.createdDate)}</td>
                            <td>{printDateTimeTwoLines(recipe?.updatedDate)}</td>
                            <td>
                              <Badge className={PRINT_LANGUAGE_COLOR[recipe?.language]}>
                                {PRINT_LANGUAGE[recipe?.language]}
                              </Badge>
                            </td>
                            <td>
                              <Badge className={recipe?.isFeatured ? 'bg-success' : 'bg-danger'}>
                                {recipe?.isFeatured ? 'Yes' : 'No'}
                              </Badge>
                            </td>
                            <td>
                              <Badge className={recipe?.active ? 'bg-success' : 'bg-danger'}>
                                {recipe?.active ? 'Yes' : 'No'}
                              </Badge>
                            </td>
                            <td>
                              <Badge className={recipe?.deleted ? 'bg-success' : 'bg-danger'}>
                                {recipe?.deleted ? 'Yes' : 'No'}
                              </Badge>
                            </td>

                            <td>
                              {user.entityPermissions?.nutrition_recipe?.update && (
                                <UncontrolledDropdown>
                                  <DropdownToggle href="#" className="card-drop" tag="i">
                                    <i role="button" className="mdi mdi-dots-vertical font-size-18" />
                                  </DropdownToggle>
                                  <DropdownMenu className="dropdown-menu-end">
                                    <DropdownItem onClick={() => handleActive(recipe)}>
                                      <i
                                        className={`fas  ${
                                          recipe?.active ? 'fa-ban text-danger' : 'fa-power-off text-success'
                                        }   m-1 pe-3`}
                                      />
                                      {recipe?.active ? 'Disable' : 'Enable'}
                                    </DropdownItem>
                                    <DropdownItem onClick={() => handleDelete(recipe)}>
                                      <i className={'fas fa-trash-alt text-danger m-1 pe-3'} />
                                      {recipe?.deleted ? 'Undelete' : 'Delete'}
                                    </DropdownItem>
                                    <Divider className="my-2" />

                                    <Link
                                      to={buildURL(SMKLinks.RECIPE_EDIT, {
                                        id: recipe?.id,
                                      })}>
                                      <DropdownItem>
                                        <i className="fas fa-pencil-alt text-success m-1 pe-3" />
                                        Edit
                                      </DropdownItem>
                                    </Link>
                                    <DropdownItem onClick={() => handleCopyStaging(recipe)}>
                                      <i className={`fas  ${'fa-copy text-primary'}   m-1 pe-3`} />
                                      Copy to staging
                                    </DropdownItem>
                                  </DropdownMenu>
                                </UncontrolledDropdown>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <TableError />
                    )}
                  </tbody>
                </Table>
              </div>
            </Col>
          </Row>

          <Paginate pageInfo={recipeListPagination} onChangePage={handleChangePage} />
        </Container>
      </div>
    </>
  );
};

export default RecipeList;
